import React, { useEffect, useRef, useState } from 'react'
import { Button } from 'primereact/button'
import Message from './Message'
import FileUploadSection from './FileUploadSection'
import PreviewModal from './PreviewModal'
import './TicketChat.scss'

const getInitials = (name: string) => {
  if (!name) return ''
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
}

const CLOSED_STATUSES = ['Closed', 'Resolved', 'Rejected', 'Completed']

const TicketChat = ({
  ticket,
  newComment,
  setNewComment,
  onSendComment,
  onCloseTicket,
  onRefreshComments,
  isLoading,
  filteredTickets,
}: {
  ticket: any
  newComment: string
  setNewComment: (val: string) => void
  onSendComment: (attachments?: File[]) => void
  onCloseTicket: () => void
  onRefreshComments: () => void
  isLoading: boolean
  filteredTickets: Record<string, any>[]
}) => {
  const isClosed = CLOSED_STATUSES.includes((ticket?.status || '').trim())
  const chatScrollRef = useRef<HTMLDivElement>(null)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [previewModalVisible, setPreviewModalVisible] = useState(false)
  const [previewImage, setPreviewImage] = useState<string | null>(null)
  const [previewFileName, setPreviewFileName] = useState<string>('')
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null)

  // Auto-scroll to bottom when comments change
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight
    }
  }, [ticket?.comments?.length])

  const handleFileUpload = (event: any) => {
    const files = Array.from(event.files) as File[]
    
    // Check total number of files (max 5)
    if (selectedFiles.length + files.length > 5) {
      alert('Maximum 5 images allowed per comment')
      return
    }
    
    // Validate files
    const validFiles = files.filter((file: File) => {
      // Check if it's an image
      if (!file.type.startsWith('image/')) {
        alert('Only image files are allowed')
        return false
      }
      
      // Check file size (5MB limit)
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB')
        return false
      }
      
      return true
    })
    
    setSelectedFiles(prev => [...prev, ...validFiles])
  }

  const handlePreviewImage = (file: File) => {
    const reader = new FileReader()

    reader.onload = (e) => {
      setPreviewImage(e.target?.result as string)
      setPreviewImageUrl(null)
      setPreviewFileName(file.name)
      setPreviewModalVisible(true)
    }

    reader.readAsDataURL(file)
  }

  const handlePreviewAttachment = (attachment: any) => {
    setPreviewImage(null)
    setPreviewImageUrl(attachment.url)
    setPreviewFileName(attachment.originalFileName)
    setPreviewModalVisible(true)
  }

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index))
  }

  const handleSendCommentWithFiles = () => {
    if (!newComment && selectedFiles.length === 0) {
      alert('Please enter a comment or select an image')
      return
    }

    onSendComment(selectedFiles.length > 0 ? selectedFiles : undefined)
    setSelectedFiles([])
  }

  return (
    <div className="ticket-chat">
      {/* Ticket title at the top */}
      <div className="ticket-chat__header">
        <span className="ticket-chat__header__title">
          {ticket?.title || 'Select a Ticket'}
        </span>
        {ticket && (
          <Button
            icon="pi pi-refresh"
            className="p-button-text ticket-chat__header__refresh-btn"
            onClick={onRefreshComments}
            tooltip="Refresh comments"
            aria-label="Refresh comments"
            tooltipOptions={{ position: 'top' }}
          />
        )}
        {!isClosed && ticket && filteredTickets?.length > 0 && (
          <Button
            label="Close Ticket"
            className="p-button-danger p-button-sm ticket-chat__header__close-btn"
            onClick={onCloseTicket}
          />
        )}
      </div>
      
      <hr className="ticket-chat__divider" />
      
      <div className="ticket-chat__content">
        {!ticket ? (
          <div className="ticket-chat__no-selection">
            <div className="ticket-chat__no-selection__icon">
              <i className="pi pi-comments" />
            </div>
            <div className="ticket-chat__no-selection__title">
              No Ticket Selected
            </div>
            <div className="ticket-chat__no-selection__message">
              Please select a ticket from the list to view its details and add comments.
            </div>
          </div>
        ) : (
          <div ref={chatScrollRef} className="ticket-chat__messages">
            {/* Show ticket description as the first message */}
            {ticket?.description && (
              <div className="message">
                <div className="message__avatar message__avatar--other">
                  {getInitials(ticket?.creator || 'User')}
                </div>
                <div className="message__content message__content--other">
                  {ticket.description}
                  <div className="message__meta">
                    {ticket?.creator || 'User'} {ticket?.createdAt ? ticket.createdAt : ''}
                  </div>
                </div>
              </div>
            )}
            
            {/* Comments */}
            {ticket?.comments?.length === 0 && !ticket?.description && (
              <div className="ticket-chat__empty-state">
                No messages yet.
              </div>
            )}
            
            {ticket?.comments?.map((msg: any, idx: number) => (
              <Message
                key={idx}
                message={msg}
                index={idx}
                onPreviewAttachment={handlePreviewAttachment}
              />
            ))}
          </div>
        )}
        
        {/* File upload and comment input */}
        <FileUploadSection
          newComment={newComment}
          setNewComment={setNewComment}
          selectedFiles={selectedFiles}
          onFileUpload={handleFileUpload}
          onRemoveFile={handleRemoveFile}
          onPreviewImage={handlePreviewImage}
          onSendComment={handleSendCommentWithFiles}
          isLoading={isLoading}
          isClosed={isClosed}
          isTicketSelected={!!ticket}
        />
      </div>
      
      {/* Image Preview Modal */}
      <PreviewModal
        visible={previewModalVisible}
        onHide={() => setPreviewModalVisible(false)}
        previewImage={previewImage}
        previewImageUrl={previewImageUrl}
        previewFileName={previewFileName}
      />
    </div>
  )
}

export default TicketChat
